import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";

const CURRENCIES = ["CAD", "USD", "EUR", "XOF", "GBP"];
const GOAL_KEYS = ["job", "cv", "learn", "content", "finance", "community"];
const GOAL_VALUES = {
  job: "trouver un emploi", cv: "améliorer mon CV", learn: "apprendre",
  content: "créer du contenu", finance: "organiser mes finances", community: "rejoindre une communauté",
};

export default function Onboarding() {
  const { t, lang, setLang } = useI18n();
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [countries, setCountries] = useState([]);
  const [language, setLanguage] = useState(lang);
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("");
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user?.settings?.onboarding_completed) navigate("/dashboard");
  }, [user, navigate]);

  const onCountry = (code) => {
    setCountry(code);
    const c = countries.find((x) => x.code === code);
    if (c) { setCurrency(c.currency); setLanguage(c.default_language); setLang(c.default_language); }
  };

  const toggleGoal = (k) => setGoals((g) => g.includes(k) ? g.filter((x) => x !== k) : [...g, k]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/users/me/onboarding", {
        language, country_code: country, currency,
        goals: goals.map((k) => GOAL_VALUES[k]),
      });
      await refreshUser();
      navigate("/dashboard");
    } catch (err) { toast.error(t("common.error")); }
    finally { setLoading(false); }
  };

  const steps = [
    { title: t("onboarding.language"), canNext: !!language },
    { title: t("onboarding.country"), canNext: !!country },
    { title: t("onboarding.currency"), canNext: !!currency },
    { title: t("onboarding.goals"), canNext: true },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-16 flex items-center px-5 lg:px-8"><Logo /></header>
      <div className="flex-1 flex items-center justify-center px-5 py-8">
        <div className="w-full max-w-lg">
          <div className="text-center mb-6">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{t("onboarding.title")}</h1>
            <p className="mt-2 text-muted-foreground text-sm">{t("onboarding.subtitle")}</p>
          </div>
          <div className="flex gap-2 mb-6">
            {steps.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
          <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="rounded-2xl border bg-card p-6 sm:p-8" data-testid={`onboarding-step-${step}`}>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t("onboarding.step")} {step + 1} {t("onboarding.of")} {steps.length}</p>
            <h2 className="font-display text-xl font-semibold mb-5">{steps[step].title}</h2>

            {step === 0 && (
              <div className="grid grid-cols-2 gap-3">
                {[{ code: "fr", label: "Français" }, { code: "en", label: "English" }].map((l) => (
                  <button key={l.code} onClick={() => { setLanguage(l.code); setLang(l.code); }} data-testid={`onboarding-lang-${l.code}`}
                    className={`p-4 rounded-xl border text-left transition-colors ${language === l.code ? "border-primary bg-primary/5" : "hover:bg-muted"}`}>
                    <span className="font-medium">{l.label}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 1 && (
              <Select value={country} onValueChange={onCountry}>
                <SelectTrigger data-testid="onboarding-country"><SelectValue placeholder={t("onboarding.country")} /></SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code} data-testid={`country-opt-${c.code}`}>
                      {lang === "fr" ? c.name_fr : c.name_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {step === 2 && (
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="onboarding-currency"><SelectValue placeholder={t("onboarding.currency")} /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c} data-testid={`currency-opt-${c}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {step === 3 && (
              <div>
                <p className="text-sm text-muted-foreground mb-4">{t("onboarding.goalsSub")}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {GOAL_KEYS.map((k) => (
                    <button key={k} onClick={() => toggleGoal(k)} data-testid={`goal-${k}`}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-colors ${goals.includes(k) ? "border-primary bg-primary/5" : "hover:bg-muted"}`}>
                      <span className={`h-5 w-5 rounded-md border flex items-center justify-center ${goals.includes(k) ? "bg-primary border-primary text-primary-foreground" : ""}`}>
                        {goals.includes(k) && <Check className="h-3.5 w-3.5" />}
                      </span>
                      {t(`onboarding.goalOptions.${k}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between">
              <Button variant="ghost" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="onboarding-back">{t("common.back")}</Button>
              {step < steps.length - 1 ? (
                <Button disabled={!steps[step].canNext} onClick={() => setStep((s) => s + 1)} className="rounded-full" data-testid="onboarding-next">
                  {t("onboarding.next")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button disabled={loading} onClick={finish} className="rounded-full" data-testid="onboarding-finish">
                  {loading ? t("common.loading") : t("onboarding.finish")}
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
